import { useTranslation } from "react-i18next";
import { Badge } from "@/shared/components/ui/badge";
import type { NotificationReadiness } from "./notification-types";

interface NotificationReadinessSectionProps {
  /** The user's phone number, if any (from the detail payload). */
  readonly phone?: string;
  readonly readiness: NotificationReadiness;
}

/**
 * Read-only panel that surfaces channel aptitude on the user detail so
 * an operator can judge deliverability *before* opening the send
 * dialog: phone + verification state, push-device presence, and the
 * per-channel opt-in snapshot.
 */
export function NotificationReadinessSection({
  phone,
  readiness,
}: NotificationReadinessSectionProps) {
  const { t } = useTranslation();
  const hasPhone = typeof phone === "string" && phone.trim().length > 0;
  const { preferences: prefs } = readiness;

  return (
    <section
      data-testid="user-notification-readiness"
      className="rounded-lg border border-border bg-card p-6"
    >
      <h2 className="brand-eyebrow mb-4">{t("users.notifyReadiness.title")}</h2>
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("users.notifyReadiness.phone")}
          </dt>
          <dd className="mt-1 flex items-center gap-2">
            <span className="font-sans text-sm font-normal text-foreground">
              {hasPhone ? phone : t("users.notifyReadiness.noPhone")}
            </span>
            {hasPhone &&
              (readiness.phoneVerified ? (
                <Badge data-testid="phone-verified-badge" variant="secondary">
                  {t("users.notifyReadiness.verified")}
                </Badge>
              ) : (
                <Badge data-testid="phone-unverified-badge" variant="outline">
                  {t("users.notifyReadiness.unverified")}
                </Badge>
              ))}
          </dd>
        </div>

        <div>
          <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("users.notifyReadiness.pushDevices")}
          </dt>
          <dd
            data-testid="push-devices-status"
            className="mt-1 font-sans text-sm font-normal text-foreground"
          >
            {readiness.hasActivePushDevice
              ? t("users.notifyReadiness.pushActive")
              : t("users.notifyReadiness.pushNone")}
          </dd>
        </div>

        <div className="sm:col-span-2">
          <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("users.notifyReadiness.preferences")}
          </dt>
          <dd className="mt-2 flex flex-wrap gap-2">
            <PrefBadge channel="PUSH" enabled={prefs.push} />
            <PrefBadge channel="EMAIL" enabled={prefs.email} />
            <PrefBadge channel="SMS" enabled={prefs.sms} />
          </dd>
        </div>
      </dl>
    </section>
  );
}

interface PrefBadgeProps {
  readonly channel: "PUSH" | "EMAIL" | "SMS";
  readonly enabled: boolean;
}

function PrefBadge({ channel, enabled }: PrefBadgeProps) {
  const { t } = useTranslation();
  const label = t(`users.notify.channel${channel.charAt(0)}${channel.slice(1).toLowerCase()}`);
  const state = enabled ? t("users.notifyReadiness.prefOn") : t("users.notifyReadiness.prefOff");
  return (
    <Badge data-testid={`pref-badge-${channel}`} variant={enabled ? "secondary" : "outline"}>
      {label}: {state}
    </Badge>
  );
}
